import { Suspense, useCallback, useContext } from 'react';
import { Dimensions, ExtraBase, SmartSendItemApiModel } from '~/lib/api/smartsend/types';
import { SmartSendContext } from '../../../lib/context/SmartSendContext';
import { SaveItemsAction } from '../../../lib/context/SmartSendContextModels';
import GetTariffSystemFromItem, { GetTariffSystemLengthFromItem } from '../../../lib/helpers/GetTariffSystemFromItem';
import { TariffSuggestions } from '../../customs/TariffSuggestions';
import { DimensionsSelection } from '../DimensionsSelection';
import { P2GBox, P2GText } from '@parcel2go/core-ui';
import UnavailableExtras from './UnavailableExtras';

interface props {
  item: SmartSendItemApiModel;
  error: string;
  children: React.ReactNode;
}

const QuickFix: React.FC<props> = (props) => {
  const { dispatch } = useContext(SmartSendContext);

  const onDimensionsSave = useCallback(
    (dims: Dimensions) =>
      dispatch(
        new SaveItemsAction([props.item.Id], ['Dimensions'], {
          Dimensions: dims
        })
      ),
    [dispatch, props.item.Id]
  );

  const onUnavailableExtrasRemoval = useCallback(
    (extras: ExtraBase[]) => {
      dispatch(
        new SaveItemsAction([props.item.Id], ['Extras'], {
          Extras: extras
        })
      );
    },
    [dispatch, props.item]
  );

  // const setDate = async (d: Date) =>
  //   dispatch(new SaveItemsAction([props.item.Id], ['PreferredCollectionDate'], { PreferredCollectionDate: d }));

  const onTariffCodeSave = useCallback(
    (id: string, code: string) => {
      const ols = [...props.item.MarketplaceOrderLines];
      ols.forEach((o) => o.Id === id && (o.Product.TariffCode = code));
      dispatch(
        new SaveItemsAction([props.item.Id], ['MarketplaceOrderLines'], {
          MarketplaceOrderLines: ols
        })
      );
    },
    [dispatch, props.item.Id, props.item.MarketplaceOrderLines]
  );

  switch (props.error) {
    case 'PRODUCT-TARIFFCODE-TOO-SHORT':
    case 'PRODUCT-TARIFFCODE-REQUIRED':
      return (
        <P2GBox display="flex" flexDirection="column" gap={2}>
          {props.children}
          <P2GText variant="caption">
            Your Tariff Code should be {GetTariffSystemLengthFromItem(props.item)} digits long.
          </P2GText>
          <Suspense>
            {props.item.MarketplaceOrderLines.map((m) => (
              <TariffSuggestions
                key={m.Id}
                title={`Suggestion for '${m.Product.Description}':`}
                canSelect={true}
                limit={1}
                selectCode={(v) => onTariffCodeSave(m.Id, v)}
                tariffSystem={GetTariffSystemFromItem(props.item)}
                itemDescription={m.Product.Description}
              />
            ))}
          </Suspense>
        </P2GBox>
      );

    case 'ITEM-WEIGHT':
      return (
        <DimensionsSelection
          dimensions={props.item.PackageDimensions}
          onSave={onDimensionsSave}
          startMinimised={false}
          dimensionsRequired={false}
          hideDimensionsIfNotRequired={true}
          showWhenTouched={true}
        />
      );

    case 'EXTRAS-UNAVAILABLE':
      return <UnavailableExtras item={props.item} onRemove={onUnavailableExtrasRemoval} />;

    default:
      return <>{props.children}</>;
  }
};

export default QuickFix;
